/**
 * API service for search functionality
 */

import { getApiClient } from "./apiClient";

export interface RelationshipFilter {
  related_to_entity: string[];
  relationship_types?: string[];
  max_relationship_tiers: number;
}

export interface SearchFilters {
  q?: string;
  entity_type?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  order?: string;
  home_country?: string[];
  naics?: string[];
  input_hs_codes?: string[];
  output_hs_codes?: string[];
  founding_year_min?: number;
  founding_year_max?: number;
  employee_count_min?: number;
  employee_count_max?: number;
  labels?: string[];
  organization_types?: string[];
  relationship_filters?: RelationshipFilter[];
  facility_types?: string[];
  facility_statuses?: string[];
  facility_owners?: string[];
  facility_owner_max_tiers?: number;
  suppliers?: string[];
  customers?: string[];
  project_types?: string[];
  project_statuses?: string[];
  participating_organizations?: string[];
  project_organization_max_tiers?: number;
  domains?: string[];
  updated_date_start?: string;
  updated_date_end?: string;
}

export interface SearchResponse {
  results: any[];
  pagination: {
    has_more: boolean;
    next_cursor?: string;
    total_count?: number;
  };
}

/**
 * Search for organizations with filters and pagination
 */
export const searchOrganizations = async (
  filters: SearchFilters,
): Promise<SearchResponse> => {
  try {
    const client = await getApiClient();
    const params: Record<string, any> = {
      entity_type: "organizations",
      ...filters,
    };

    // Convert array filters to comma-separated strings
    if (filters.home_country?.length) {
      params.home_country = filters.home_country.join(",");
    }
    if (filters.naics?.length) {
      params.naics = filters.naics.join(",");
    }
    if (filters.input_hs_codes?.length) {
      params.input_hs_codes = filters.input_hs_codes.join(",");
    }
    if (filters.output_hs_codes?.length) {
      params.output_hs_codes = filters.output_hs_codes.join(",");
    }
    if (filters.labels?.length) {
      params.labels = filters.labels.join(",");
    }
    if (filters.organization_types?.length) {
      params.organization_types = filters.organization_types.join(",");
    }
    // Handle structured relationship filters (send as JSON)
    if (filters.relationship_filters?.length) {
      params.relationship_filters = JSON.stringify(
        filters.relationship_filters,
      );
    }
    // Handle project organization filter
    if (filters.participating_organizations?.length) {
      params.participating_organizations =
        filters.participating_organizations.join(",");
    }
    if (filters.project_organization_max_tiers) {
      params.project_organization_max_tiers =
        filters.project_organization_max_tiers;
    }

    const response = await client.get("/graph/search/", { params });
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

/**
 * Search for facilities with filters and pagination
 */
export const searchFacilities = async (
  filters: SearchFilters,
): Promise<SearchResponse> => {
  try {
    const params: Record<string, string> = {};

    if (filters.q) params.q = filters.q;
    if (filters.entity_type) params.entity_type = filters.entity_type;
    if (filters.limit) params.limit = filters.limit.toString();
    if (filters.offset) params.offset = filters.offset.toString();
    if (filters.sort) params.sort = filters.sort;
    if (filters.order) params.order = filters.order;
    if (filters.updated_date_start)
      params.updated_date_start = filters.updated_date_start;
    if (filters.updated_date_end)
      params.updated_date_end = filters.updated_date_end;

    // Facility-specific filters
    if (filters.home_country)
      params.home_country = filters.home_country.join(",");
    if (filters.naics) params.naics = filters.naics.join(",");
    if (filters.input_hs_codes)
      params.input_hs_codes = filters.input_hs_codes.join(",");
    if (filters.output_hs_codes)
      params.output_hs_codes = filters.output_hs_codes.join(",");
    if (filters.facility_types)
      params.facility_types = filters.facility_types.join(",");
    if (filters.facility_statuses)
      params.facility_statuses = filters.facility_statuses.join(",");
    if (filters.facility_owners)
      params.facility_owners = filters.facility_owners.join(",");
    if (filters.facility_owner_max_tiers)
      params.facility_owner_max_tiers =
        filters.facility_owner_max_tiers.toString();
    if (filters.suppliers) params.suppliers = filters.suppliers.join(",");
    if (filters.customers) params.customers = filters.customers.join(",");
    if (filters.labels) params.labels = filters.labels.join(",");

    // Structured relationship filters (send as JSON)
    if (filters.relationship_filters) {
      params.relationship_filters = JSON.stringify(
        filters.relationship_filters,
      );
    }

    const client = await getApiClient();
    const response = await client.get("/graph/search/", { params });

    // Transform backend response to match frontend SearchResponse format
    return {
      results: response.data.results || [],
      pagination: {
        has_more: response.data.pagination?.has_more || false,
        total_count: response.data.pagination?.total_count || 0,
      },
    };
  } catch (error: any) {
    throw error;
  }
};

export const searchProjects = async (
  filters: SearchFilters,
): Promise<SearchResponse> => {
  try {
    const params: Record<string, any> = {
      entity_type: "projects",
      ...filters,
    };

    // Convert array filters to comma-separated strings
    if (filters.project_types?.length) {
      params.project_types = filters.project_types.join(",");
    }
    if (filters.project_statuses?.length) {
      params.project_statuses = filters.project_statuses.join(",");
    }
    if (filters.naics?.length) {
      params.naics = filters.naics.join(",");
    }
    if (filters.home_country?.length) {
      params.home_country = filters.home_country.join(",");
    }
    // Handle project organization filter
    if (filters.participating_organizations?.length) {
      params.participating_organizations =
        filters.participating_organizations.join(",");
    }
    if (filters.project_organization_max_tiers) {
      params.project_organization_max_tiers =
        filters.project_organization_max_tiers;
    }

    const client = await getApiClient();
    const response = await client.get("/graph/search/", { params });

    // Transform backend response to match frontend SearchResponse format
    return {
      results: response.data.results || [],
      pagination: {
        has_more: response.data.pagination?.has_more || false,
        total_count: response.data.pagination?.total_count || 0,
      },
    };
  } catch (error: any) {
    throw error;
  }
};

export const searchSources = async (
  filters: SearchFilters,
): Promise<SearchResponse> => {
  try {
    const params: Record<string, any> = {
      entity_type: "sources",
      ...filters,
    };

    // Convert array filters to comma-separated strings
    if (filters.domains?.length) {
      params.domains = filters.domains.join(",");
    }

    const client = await getApiClient();
    const response = await client.get("/graph/search/", { params });

    // Transform backend response to match frontend SearchResponse format
    return {
      results: response.data.results || [],
      pagination: {
        has_more: response.data.pagination?.has_more || false,
        total_count: response.data.pagination?.total_count || 0,
      },
    };
  } catch (error: any) {
    throw error;
  }
};

/**
 * Get organization suggestions for autocomplete
 */
export interface OrganizationSuggestion {
  id: string;
  name: string;
  country?: string;
  country_iso2?: string;
  display_label: string;
}

export interface AutocompleteResponse {
  suggestions: OrganizationSuggestion[];
  count: number;
  query: string;
}

export const getOrganizationSuggestions = async (
  query: string,
  limit: number = 20,
): Promise<AutocompleteResponse> => {
  try {
    const params = {
      q: query,
      limit: limit.toString(),
    };

    const client = await getApiClient();
    const response = await client.get(
      "/graph/search/organizations/autocomplete/",
      {
        params,
      },
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};
